
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, RefreshCw, Users } from "lucide-react";
import { useAdminReferrals } from '@/hooks/useAdminAffiliate';
import { TransferReferralDialog } from './TransferReferralDialog';
import { formatDate } from '@/utils/formatters';
import { useIsMobile } from '@/hooks/use-mobile';
import { TablePagination } from '@/components/ui/table-pagination';
import { Skeleton } from '@/components/ui/skeleton';

export const AssignedUsersTable = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [transferDialogUser, setTransferDialogUser] = useState<any>(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const isMobile = useIsMobile();
  
  const { data: referralsResponse, isLoading, refetch } = useAdminReferrals('all', currentPage, pageSize);
  
  const referrals = referralsResponse?.data || [];
  const totalItems = referralsResponse?.total || 0;

  // Client-side filtering only on current page
  const filteredReferrals = referrals.filter(referral => 
    referral.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    referral.affiliateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    referral.referralCode.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleTransferUser = (referral: any) => {
    console.log('🔄 Preparing to transfer user:', {
      referralId: referral.id,
      referredUserId: referral.referred_user_id,
      userName: referral.userName,
      currentAffiliate: referral.affiliateName
    });

    setTransferDialogUser({
      id: referral.referred_user_id || referral.id,
      masked_name: referral.userName,
      masked_email: referral.userName,
      current_affiliate: referral.affiliateName
    });
    setTransferDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'converted':
        return 'bg-green-100 text-green-800';
      case 'registered':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Assigned Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-4">
              <Skeleton className="h-10 flex-1" />
              <Skeleton className="h-10 w-24" />
            </div>
            {Array(10).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Assigned Users ({totalItems})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users or affiliates..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {filteredReferrals.length > 0 ? (
            isMobile ? (
              // Mobile card layout
              <div className="space-y-3">
                {filteredReferrals.map((referral) => (
                  <div key={referral.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{referral.userName}</div>
                        <div className="text-sm text-muted-foreground">{referral.affiliateName}</div>
                      </div>
                      <Badge className={getStatusColor(referral.status)}>
                        {referral.status}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        Code: {referral.referralCode}
                      </div>
                      <Button size="sm" onClick={() => handleTransferUser(referral)}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Transfer
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Desktop table layout
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Current Affiliate</TableHead>
                      <TableHead>Referral Code</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Assigned Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReferrals.map((referral) => (
                      <TableRow key={referral.id}>
                        <TableCell>{referral.userName}</TableCell>
                        <TableCell>{referral.affiliateName}</TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-1 py-0.5 rounded">
                            {referral.referralCode}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(referral.status)}>
                            {referral.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(new Date(referral.createdAt))}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" onClick={() => handleTransferUser(referral)}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Transfer
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              No assigned users found
            </div>
          )}

          <TablePagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            className="mt-4"
          />
        </CardContent>
      </Card>

      <TransferReferralDialog
        user={transferDialogUser}
        open={transferDialogOpen}
        onOpenChange={setTransferDialogOpen}
      />
    </>
  );
};
